import {
  Color,
  Quat,
  Vec3 as PcVec3,
  XRPAD_B,
  type CameraComponent,
  type Entity,
} from 'playcanvas';
import { XrControllers } from 'playcanvas/scripts/esm/xr-controllers.mjs';
import { XrNavigation } from 'playcanvas/scripts/esm/xr-navigation.mjs';
import type { Collision } from '../../submodules/supersplat-viewer/src/collision';
import type { Global } from '../../submodules/supersplat-viewer/src/types';
import type { App } from '../../submodules/supersplat-viewer/src/app';
import type { SuperSplatRuntime } from './types';
import type { VrQualityPreset } from '@mindcloudxai/shared';
import { resolveVrQualityConfig, type VrQualityConfig } from './constants';

// 默认 VR 性能档位：Quest 3 等主流头显的实测安全值
const DEFAULT_VR_PRESET: VrQualityPreset = 'medium';

// 模块级共享：initXrLifecycle 的 xr.on('start') 和 enterVrSession 都需要读写
let currentVrConfig: VrQualityConfig = resolveVrQualityConfig(DEFAULT_VR_PRESET);

export interface XrLifecycleContext {
  app: App;
  camera: Entity;
  canvas: HTMLCanvasElement;
  global: Global;
  getViewer: () => SuperSplatRuntime | undefined;
  applyCanvasSize: () => void;
}

export function initXrLifecycle(ctx: XrLifecycleContext): void {
  const { app, camera, canvas, global } = ctx;
  const xr = app.xr;
  const cameraComp = camera.camera;
  if (!xr || !cameraComp) return;

  const { events, state } = global;
  state.hasVR = xr.isAvailable('immersive-vr');
  xr.on('available:immersive-vr', (available: boolean) => {
    state.hasVR = available;
  });
  events.on('inputEvent', (event: string) => {
    if (event === 'cancel' && xr.active) {
      xr.end();
    }
  });
  if (global.renderer !== 'webgl') return;

  const parent = camera.parent as Entity;
  if (!parent.script) {
    parent.addComponent('script');
  }
  parent.script?.create(XrControllers);
  parent.script?.create(XrNavigation);

  const parentPosition = new PcVec3();
  const parentRotation = new Quat();
  const cameraPosition = new PcVec3();
  const cameraRotation = new Quat();
  const angles = new PcVec3();
  const clearColor = new Color();
  const canvasParent = canvas.parentElement;
  const canvasNextSibling = canvas.nextSibling;
  let avatarEnabledBeforeXr = false;
  let hasCollisionBeforeXr = false;
  let hasCollisionOverlayBeforeXr = false;
  let collisionOverlayEnabledBeforeXr = false;
  let walkAllowedBeforeXr = false;
  let inputCollisionBeforeXr: Collision | null | undefined;
  let xrExitButtonPressed = false;
  // XR 启动时保存的 splatBudget，XR 结束后恢复
  let savedSplatBudget = 0;

  app.on('update', () => {
    if (!xr.active) {
      xrExitButtonPressed = false;
      return;
    }
    const pressed = xr.input.inputSources.some((inputSource) => {
      const gamepad = inputSource.gamepad;
      return inputSource.handedness === 'right' && !!gamepad?.buttons[XRPAD_B]?.pressed;
    });
    if (pressed && !xrExitButtonPressed) {
      xr.end();
    }
    xrExitButtonPressed = pressed;
  });

  xr.on('start', () => {
    if (!ctx.global) return;
    app.autoRender = true;
    if (typeof xr.fixedFoveation === 'number') {
      xr.fixedFoveation = currentVrConfig.fixedFoveation;
    }
    avatarEnabledBeforeXr = ctx.global.state.avatarEnabled;
    hasCollisionBeforeXr = ctx.global.state.hasCollision;
    hasCollisionOverlayBeforeXr = ctx.global.state.hasCollisionOverlay;
    collisionOverlayEnabledBeforeXr = ctx.global.state.collisionOverlayEnabled;
    walkAllowedBeforeXr = ctx.global.state.walkAllowed;
    inputCollisionBeforeXr = ctx.getViewer()?.inputController?.collision;
    ctx.global.state.avatarEnabled = false;
    ctx.global.state.hasCollision = false;
    ctx.global.state.hasCollisionOverlay = false;
    ctx.global.state.collisionOverlayEnabled = false;
    ctx.global.state.walkAllowed = false;
    if (ctx.getViewer()?.inputController) {
      ctx.getViewer()!.inputController!.collision = null;
    }
    // 保存并覆盖 splatBudget，VR 立体渲染需要更低的渲染预算
    savedSplatBudget = saveAndApplyVrGsplatSettings(app, currentVrConfig.splatBudget);
    parentPosition.copy(parent.getPosition());
    parentRotation.copy(parent.getRotation());
    cameraPosition.copy(camera.getPosition());
    cameraRotation.copy(camera.getRotation());
    cameraRotation.getEulerAngles(angles);
    parent.setPosition(cameraPosition.x, 0, cameraPosition.z);
    parent.setEulerAngles(0, angles.y, 0);
    if (xr.type === 'immersive-ar') {
      clearColor.copy(cameraComp.clearColor);
      cameraComp.clearColor = new Color(0, 0, 0, 0);
    }
  });

  xr.on('end', () => {
    xrExitButtonPressed = false;
    if (ctx.global) {
      ctx.global.state.avatarEnabled = avatarEnabledBeforeXr;
      ctx.global.state.hasCollision = hasCollisionBeforeXr;
      ctx.global.state.hasCollisionOverlay = hasCollisionOverlayBeforeXr;
      ctx.global.state.collisionOverlayEnabled = collisionOverlayEnabledBeforeXr;
      ctx.global.state.walkAllowed = walkAllowedBeforeXr;
    }
    if (ctx.getViewer()?.inputController) {
      ctx.getViewer()!.inputController!.collision = inputCollisionBeforeXr ?? null;
    }
    restoreGsplatSettings(app, savedSplatBudget);
    savedSplatBudget = 0;
    app.autoRender = false;
    parent.setPosition(parentPosition);
    parent.setRotation(parentRotation);
    camera.setPosition(cameraPosition);
    camera.setRotation(cameraRotation);
    if (xr.type === 'immersive-ar') {
      cameraComp.clearColor = clearColor;
    }
    requestAnimationFrame(() => {
      if (canvasParent) {
        if (canvasNextSibling?.parentElement === canvasParent) {
          canvasParent.insertBefore(canvas, canvasNextSibling);
        } else {
          canvasParent.appendChild(canvas);
        }
      }
      ctx.applyCanvasSize();
    });
  });

  // 场景可能在 XR 期间完成加载，supersplat-viewer 的 applyPerfSettings
  // 会覆盖 VR 专用 gsplat 设置，需要重新应用
  global.events.on('firstFrame', () => {
    if (app.xr?.active && savedSplatBudget > 0) {
      applyVrGsplatSettings(app, currentVrConfig.splatBudget);
    }
  });
}

export function enterVrSession(
  app: App,
  camera: Entity,
  global: Global,
  disposed: boolean,
  preset?: VrQualityPreset,
): void {
  const xr = app.xr;
  const cameraComp = camera.camera;
  if (!global || disposed || global.renderer !== 'webgl' || !xr || xr.active || !cameraComp) {
    return;
  }
  currentVrConfig = resolveVrQualityConfig(preset ?? DEFAULT_VR_PRESET);
  cameraComp.nearClip = 0.01;
  cameraComp.farClip = 1000;
  xr.start(cameraComp as CameraComponent, 'immersive-vr', 'local-floor', {
    framebufferScaleFactor: currentVrConfig.framebufferScale,
  });
}

/** 保存当前 splatBudget 并应用 VR 专用低预算。 */
function saveAndApplyVrGsplatSettings(app: App, budget: number): number {
  const gsplat = app.scene?.gsplat;
  if (!gsplat) return 0;
  const saved = gsplat.splatBudget;
  gsplat.splatBudget = budget;
  return saved;
}

/** 重新应用 VR 专用 splatBudget（场景在 XR 期间完成加载时调用）。 */
function applyVrGsplatSettings(app: App, budget: number): void {
  const gsplat = app.scene?.gsplat;
  if (!gsplat) return;
  gsplat.splatBudget = budget;
}

/** 恢复 XR 启动前的 splatBudget。 */
function restoreGsplatSettings(app: App, savedSplatBudget: number): void {
  if (savedSplatBudget <= 0) return;
  const gsplat = app.scene?.gsplat;
  if (!gsplat) return;
  gsplat.splatBudget = savedSplatBudget;
}
